import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { ApiError, endpoints } from "./api";
import type { User } from "./types";

interface AuthValue {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string, remember?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  setUser: (u: User) => void;
  can: (permission: string) => boolean;
}

const AuthCtx = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setUser(await endpoints.me());
    } catch (err) {
      if (!(err instanceof ApiError)) console.error(err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email: string, password: string, remember = false) => {
    setUser(await endpoints.login(email, password, remember));
  }, []);

  const logout = useCallback(async () => {
    try {
      await endpoints.logout();
    } finally {
      setUser(null);
    }
  }, []);

  const can = useCallback(
    (permission: string) => !!user && (user.isPlatformAdmin || user.permissions.includes(permission)),
    [user],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refresh, setUser, can }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
